import { Link } from 'react-router-dom';
import { HYPE_LIST, MOOD_LIST, RACKSAVANT, formatCount } from '@racksavant/core';
import { ONBOARDING, PRIVACY_PROMISES } from '@racksavant/ui';
import { cx } from '../cx';
import { Reveal, Wipe } from '../components/Reveal';
import { ChaosField } from '../components/ChaosField';
import { Cutout, Face, Plate } from '../components/Photo';
import { Marquee } from '../components/Marquee';
import { DeviceDeck } from '../components/DeviceDeck';
import { MoodRail } from '../components/MoodRail';
import { useStats } from '../hooks/useStats';
import { FittingRoom } from '../components/FittingRoom';
import { MoodReadCard } from '../components/MoodReadCard';
import { DesktopDownload } from '../components/DesktopDownload';
import { PLAN_VIEWS, PlanCard } from '../components/Plans';
import { ArrowRight } from '../components/icons';
import ui from '../ui.module.css';
import s from './LandingPage.module.css';

const MARQUEE_NAMES = ['maya', 'jo', 'ava', 'tunde', 'sami', 'ren', 'kit'];

const ACTS = [
  { copy: ONBOARDING.step1, note: 'It stays private. Deleting it deletes everything made from it.' },
  { copy: ONBOARDING.step2, note: `${MOOD_LIST.length} of them. Each one is also a lighting instruction.` },
  {
    copy: ONBOARDING.step3,
    note: `${RACKSAVANT.roomTtlMs / 3_600_000} hours, six letters, everyone watching it land.`,
  },
];

const ROOM_BULLETS = [
  {
    title: 'Live, not later',
    body: 'Every look lands on their screens as it develops. Nobody is waiting on a screenshot.',
  },
  {
    title: 'Hype, not likes',
    body: 'Seven reactions with an opinion inside them. “Called you a menace” is information. A heart is not.',
  },
  {
    title: 'Ephemeral on purpose',
    body: 'Rooms close after twelve hours. Nothing becomes an archive you have to curate.',
  },
];

const READ_BULLETS = [
  {
    title: 'It talks about the clothes',
    body: 'Never about you. No sizes, no measurements, no notes on your appearance. That rail lives in the model prompt, not in a policy page.',
  },
  {
    title: 'It is specific',
    body: '“Oxblood over charcoal, one line of brass at the cuff” is useful. “Great fit” is noise.',
  },
  {
    title: 'It arrives with a caption',
    body: 'Six tones, deadpan through devastating. Copy, paste, post, close the app.',
  },
];

const PROMISE_NOTES = [
  'A look is visible to you and nobody else until you post it, share a link, or drop it into a room.',
  'Not ours, not anyone else’s. Your photos are an input to your fitting and nothing more.',
  'Remove a body shot and every look generated from it is destroyed in the same operation. No orphans, no shadow copies.',
  'We do not estimate your size, your weight or your proportions. There is no field for it, because we never wanted one.',
];

const CREATOR_STATS = [
  { value: '3 min', label: 'Idea to a story-ready export. On your phone. On a Tuesday.' },
  { value: 'Six', label: 'Caption tones, deadpan through devastating.' },
  { value: '9:16 · 4:5', label: 'Cut for stories and for feed. No watermark on paid plans.' },
  { value: 'Yours', label: 'Commercial licence on Studio. Make looks you can sell against.' },
];

export function LandingPage() {
  const stats = useStats();

  return (
    <>
      {/* ---------------------------------------------------------- Hero */}
      <section className={s.hero} aria-labelledby="hero-title">
        <div className={cx(ui.container, s.heroGrid)}>
          <div className={s.heroCopy}>
            <p className={s.heroTag}>
              <span className={ui.liveDot} aria-hidden="true" />
              Fitting rooms are live
            </p>

            <h1 id="hero-title" className={s.heroTitle}>
              <Wipe
                lines={[
                  <span key="1" className={s.heroSmall}>
                    Try on the
                  </span>,
                ]}
              />
              <ChaosField text="feeling" italic className={s.heroChaos} />
            </h1>

            <p className={s.heroSub}>
              Your photo. Any garment. The mood you were reaching for — and a read on what it is
              actually doing.
            </p>

            <div className={s.heroCtas}>
              <Link to="/auth" className={cx(ui.btn, ui.primary)}>
                Open the studio
                <ArrowRight size={16} />
              </Link>
              <a href="#how" className={cx(ui.btn, ui.ghost)}>
                How it works
              </a>
            </div>

            <ul className={s.heroTrust}>
              <li>Free to start · eight fittings a day</li>
              <li>No card · no trial countdown</li>
              <li>Your photos stay private</li>
            </ul>
          </div>

          <div className={s.heroArt}>
            <Plate id="hero" className={s.heroPlate} eager caption={false} />
            <Face kind="obsessed" size={104} tilt={-7} className={s.heroFaceA} />
            <Face kind="soft_launch" size={86} tilt={5} className={s.heroFaceB} />
            <Cutout id="coat" width={228} tilt={-5} className={s.heroCoat} />
            <p className={s.heroCredit}>
              Oxblood over black. <em>The coat is doing the talking.</em>
            </p>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------ The deck */}
      <section className={s.deckSection} aria-labelledby="deck-title">
        <div className={cx(ui.container, s.deckGrid)}>
          <div className={s.deckHead}>
            <span className={ui.crop} aria-hidden="true" />
            <p className={ui.eyebrow}>The product, running</p>
            <h2 id="deck-title" className={ui.h2}>
              Not a mockup. The <em className={ui.it}>actual</em> thing.
            </h2>
            <p className={ui.lede}>
              Six surfaces, recorded off the live app on a phone and on a desktop. Drag the top card
              to deal the next one.
            </p>
          </div>
          <DeviceDeck className={s.deck} />
        </div>
      </section>

      {/* ------------------------------------------------------- Marquee */}
      <Marquee duration={30000} className={s.hypeBand}>
        {HYPE_LIST.map((h, i) => (
          <span key={h.kind} className={s.hypeItem}>
            <Face kind={h.kind} size={44} tilt={i % 2 ? 4 : -4} />
            <span>
              <b>{MARQUEE_NAMES[i % MARQUEE_NAMES.length]}</b> {h.phrase}
            </span>
            <span className={s.hypeSep} aria-hidden="true" />
          </span>
        ))}
      </Marquee>

      {/* --------------------------------------------------------- Moods */}
      <section id="moods" className={ui.section} aria-labelledby="moods-title">
        <div className={cx(ui.container, s.moodHead)}>
          <Reveal className={s.head}>
            <span className={ui.crop} aria-hidden="true" />
            <p className={ui.eyebrow}>The taxonomy</p>
            <h2 id="moods-title" className={cx(ui.h2, ui.misreg)}>
              Nobody has ever stood in front of a wardrobe thinking{' '}
              <em className={ui.flashWord}>medium</em>.
            </h2>
            <p className={ui.lede}>
              So the rack is not organised by size, or category, or whatever a retailer decided a
              capsule was this season. It is organised by {MOOD_LIST.length} moods. Pick the one
              you are reaching for; everything rearranges around it.
            </p>
          </Reveal>
          <Cutout id="dress" width={210} tilt={4} className={s.moodCutout} />
        </div>

        <div className={ui.container}>
          <Reveal delay={80}>
            <MoodRail />
          </Reveal>
          {/* Live count, or nothing. The sentence above is complete without it. */}
          {stats && stats.garments > 0 ? (
            <p className={s.rackCount}>{formatCount(stats.garments)} pieces on the rack today.</p>
          ) : null}
        </div>
      </section>

      {/* ------------------------------------------------------ How it works */}
      <section id="how" className={ui.section} aria-labelledby="how-title">
        <div className={cx(ui.container, s.actsGrid)}>
          <Reveal className={s.actsHead}>
            <span className={ui.crop} aria-hidden="true" />
            <p className={ui.eyebrow}>Three acts</p>
            <h2 id="how-title" className={ui.h2}>
              One photo. After that it is only choosing.
            </h2>
            <Plate id="mirror" className={s.actsPlate} ratio={4 / 5} />
          </Reveal>

          <ol className={s.acts}>
            {ACTS.map(({ copy, note }, i) => (
              <li key={copy.title}>
                <Reveal className={s.act} delay={i * 90}>
                  <span className={s.actIndex}>{String(i + 1).padStart(2, '0')}</span>
                  <h3 className={s.actTitle}>{copy.title}</h3>
                  <p className={s.actBody}>{copy.body}</p>
                  <p className={s.actNote}>{note}</p>
                </Reveal>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ----------------------------------------------------- Full bleed */}
      <section className={s.bleed} aria-labelledby="bleed-title">
        <Plate id="group" className={s.bleedPlate} caption={false} ratio={16 / 10} focus="50% 40%" />
        <div className={cx(ui.container, s.bleedCopy)}>
          <h2 id="bleed-title" className={s.bleedTitle}>
            <Wipe
              lines={[
                <span key="a">Four floors,</span>,
                <span key="b">three opinions,</span>,
                <span key="c">
                  one <em>decision</em>.
                </span>,
              ]}
            />
          </h2>
          <p className={s.bleedCaption}>
            Three friends in a lift, deciding. Everything below is software for that.
          </p>
        </div>
      </section>

      {/* ---------------------------------------------------- Fitting rooms */}
      <section id="rooms" className={ui.section} aria-labelledby="rooms-title">
        <div className={cx(ui.container, s.split)}>
          <Reveal className={s.splitCopy}>
            <span className={ui.crop} aria-hidden="true" />
            <p className={ui.eyebrow}>Fitting rooms</p>
            <h2 id="rooms-title" className={ui.h2}>
              Getting dressed was never a <em className={ui.oxWord}>solo</em> activity.
            </h2>
            <p className={ui.lede}>
              You already send the mirror shot to three people and then stand there holding the
              phone. We removed the standing there. Open a room, put the code in the group chat.
            </p>

            <ol className={s.bullets}>
              {ROOM_BULLETS.map((bullet, i) => (
                <li key={bullet.title} className={s.bullet}>
                  <span className={s.bulletIndex}>{String(i + 1).padStart(2, '0')}</span>
                  <p className={s.bulletTitle}>{bullet.title}</p>
                  <p className={s.bulletBody}>{bullet.body}</p>
                </li>
              ))}
            </ol>

            <Link to="/features" className={cx(ui.textLink, s.splitCta)}>
              Everything the studio does
              <ArrowRight size={16} />
            </Link>
          </Reveal>

          <Reveal className={s.splitVisual} delay={100}>
            <FittingRoom plate="duo" />
          </Reveal>
        </div>
      </section>

      {/* ------------------------------------------------------- Mood read */}
      <section id="read" className={ui.section} aria-labelledby="read-title">
        <div className={cx(ui.container, s.split, s.splitReverse)}>
          <Reveal className={s.splitCopy}>
            <span className={ui.crop} aria-hidden="true" />
            <p className={ui.eyebrow}>The payload</p>
            <h2 id="read-title" className={ui.h2}>
              The image is half of what you came for.
            </h2>
            <p className={ui.lede}>
              Every finished look comes back with a read. A verdict written to be screenshot. An
              energy number. The adjectives it is actually giving. The colour story that makes it
              work. And a caption you can paste without touching it.
            </p>

            <ol className={s.bullets}>
              {READ_BULLETS.map((bullet, i) => (
                <li key={bullet.title} className={s.bullet}>
                  <span className={s.bulletIndex}>{String(i + 1).padStart(2, '0')}</span>
                  <p className={s.bulletTitle}>{bullet.title}</p>
                  <p className={s.bulletBody}>{bullet.body}</p>
                </li>
              ))}
            </ol>

            <Plate id="detail" className={s.readPlate} ratio={16 / 9} focus="40% 30%" />
          </Reveal>

          <Reveal className={s.splitVisual} delay={100}>
            <MoodReadCard />
          </Reveal>
        </div>
      </section>

      {/* -------------------------------------------------------- Creators */}
      <section id="creators" className={s.creators} aria-labelledby="creators-title">
        <div className={cx(ui.container, s.creatorGrid)}>
          <Reveal className={s.creatorCopy}>
            <span className={ui.crop} aria-hidden="true" />
            <p className={ui.eyebrow}>For people who post</p>
            <h2 id="creators-title" className={cx(ui.h2, ui.misreg)}>
              Your feed, before you <em className={ui.it}>shoot</em> it.
            </h2>
            <p className={ui.lede}>
              If your work is being looked at, the expensive part is not the clothes. It is the day
              you lose to a shoot that was never going to work. Try the idea first. Pull the ones
              that land. Publish drops your followers can wear themselves.
            </p>
            <Link to="/creators" className={cx(ui.textLink, s.splitCta)}>
              What creators get
              <ArrowRight size={16} />
            </Link>
          </Reveal>

          <Reveal className={s.creatorArt} delay={80}>
            <Plate id="guyStreet" ratio={3 / 4} />
          </Reveal>

          <dl className={s.statRow}>
            {CREATOR_STATS.map((stat) => (
              <div key={stat.value} className={s.stat}>
                <dt className={s.statValue}>{stat.value}</dt>
                <dd className={s.statLabel}>{stat.label}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* ---------------------------------------------------------- Rails */}
      <section id="rails" className={ui.section} aria-labelledby="rails-title">
        <div className={cx(ui.container, s.railsHead)}>
          <Reveal className={s.head}>
            <span className={ui.crop} aria-hidden="true" />
            <p className={ui.eyebrow}>The rails</p>
            <h2 id="rails-title" className={ui.h2}>
              A mirror that never edits <em className={ui.flashWord}>you</em>.
            </h2>
            <p className={ui.lede}>
              This is built for people who already feel watched. Two rules are hard-coded rather
              than aspirational: the model never alters your face, body, pose or skin, and nothing
              it writes is ever about your body. Only about the clothes.
            </p>
          </Reveal>

          <Reveal delay={90}>
            <Plate id="street" className={s.railsPlate} ratio={4 / 5} />
          </Reveal>
        </div>

        <div className={ui.container}>
          <ol className={s.promises}>
            {PRIVACY_PROMISES.map((promise, i) => (
              <li key={promise}>
                <Reveal className={s.promise} delay={i * 70}>
                  <span className={s.promiseIndex}>{String(i + 1).padStart(2, '0')}</span>
                  <p className={s.promiseText}>{promise}</p>
                  <p className={s.promiseNote}>{PROMISE_NOTES[i]}</p>
                </Reveal>
              </li>
            ))}
          </ol>

          <Reveal className={s.railsFoot}>
            <Link to="/privacy" className={ui.textLink}>
              Read the privacy policy
              <ArrowRight size={16} />
            </Link>
            <span className={ui.small}>Plain English, with a summary next to every section.</span>
          </Reveal>
        </div>
      </section>

      {/* --------------------------------------------------------- Pricing */}
      <section id="pricing" className={ui.section} aria-labelledby="pricing-title">
        <div className={ui.container}>
          <Reveal className={s.pricingHead}>
            <div>
              <span className={ui.crop} aria-hidden="true" />
              <p className={ui.eyebrow}>Pricing</p>
              <h2 id="pricing-title" className={ui.h2}>
                Free until it is worth paying for.
              </h2>
            </div>
            <Link to="/pricing" className={ui.textLink}>
              Compare everything
              <ArrowRight size={16} />
            </Link>
          </Reveal>

          <Reveal delay={80} className={s.teaserGrid}>
            {PLAN_VIEWS.map((plan) => (
              <PlanCard key={plan.id} plan={plan} featured={plan.id === 'muse'} maxFeatures={4} />
            ))}
          </Reveal>

          <div className={s.pricingFoot}>
            <p className={ui.small}>
              Cancel any time. Yearly billing gives you two months back. Student pricing on request.
            </p>
            <Cutout id="bag" width={104} tilt={-5} className={s.pricingCutout} />
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- Final CTA */}
      <section className={s.final} aria-labelledby="final-title">
        <Plate id="guy" className={s.finalPlate} caption={false} ratio={16 / 9} focus="50% 30%" />
        <div className={cx(ui.container, s.finalInner)}>
          <h2 id="final-title" className={s.finalTitle}>
            <Wipe
              lines={[
                <span key="a">Go and look at yourself</span>,
                <span key="b">
                  on <em>purpose</em>.
                </span>,
              ]}
            />
          </h2>
          <p className={s.finalSub}>
            One photo. {MOOD_LIST.length} moods. A group chat with opinions. Start free —{' '}
            {RACKSAVANT.freeFittingsPerDay} fittings a day, no card, and delete means delete.
          </p>
          <div className={s.finalCtas}>
            <Link to="/auth" className={cx(ui.btn, ui.primary)}>
              Open the studio
              <ArrowRight size={16} />
            </Link>
            <Link to="/manifesto" className={cx(ui.btn, ui.ghost)}>
              Read the manifesto
            </Link>
          </div>
          <DesktopDownload className={s.finalDownload} />
          <Cutout id="jacket" width={220} tilt={-4} className={s.finalCutout} />
        </div>
      </section>
    </>
  );
}
